import { Page } from '@app/shared/models/page';
import { PageDefinition } from '@app/shared/models/page-definition';
import { sortBy, sortedBy, withoutEntry } from '@app/utils/common';
import { filterExclusive } from '@app/utils/filter/filter';
import { PagedList } from '@app/utils/lists/paged-list/paged-list';
import { PagedListOptions } from '@app/utils/lists/paged-list/paged-list-options';
import { loggerFor } from '@app/utils/logger';
import { WithKey } from '@app/utils/object-extensions/with-key';
import { WithMetadata } from '@app/utils/object-extensions/with-metadata';
import { normalizeSortOrders, SortOrderObject } from '@app/utils/sort/compare';
import log from 'loglevel';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export class PagedListImpl<T> implements PagedList<T> {
  entries: T[] = [];
  filteredEntries: T[] = [];

  private defaultOptions: PagedListOptions<T> = {
    page: 1,
    pageSize: 10,
  };

  /**
   * Remember the last filter value so if the entries are updated we can filter those
   */
  private filterValue: string = '';

  /**
   * Entries that did not match the current filter
   */
  private hiddenEntries: T[] = [];

  /**
   * Current page
   */
  private page: number;

  /**
   * Amount of pages
   */
  private pages: number;

  /**
   * Current page size
   */
  private pageSize: number;

  /**
   * Properties to sort by
   */
  private sortOrder: SortOrderObject<T>[] = [];

  private log: log.Logger;
  private updatedFilteredEntriesManually: boolean = false;

  private _lastInsertLocation?: number = undefined;

  /**
   * The page number of the entry that was added last.
   */
  get lastInsertLocation() {
    return this._lastInsertLocation;
  }

  private _order$: Subject<SortOrderObject<T>[]> = new BehaviorSubject([]);

  get order$(): Observable<SortOrderObject<T>[]> {
    return this._order$.asObservable();
  }

  /**
   * Emit the current page on change
   */
  private _page$: Subject<Page<T>> = new BehaviorSubject(undefined);

  get page$(): Observable<Page<T>> {
    return this._page$.asObservable();
  }

  constructor(options?: PagedListOptions<T>) {
    options = Object.assign({}, this.defaultOptions, options);

    this.entries = options.entries ?? [];
    this.page = options.page;
    this.pageSize = options.pageSize;

    if (options.name != null) {
      this.log = loggerFor(`PagedList "${options.name}"`);
    } else {
      this.log = loggerFor('PagedList');
    }

    if (options.sort != null) {
      this.sortOrder = normalizeSortOrders(options.sort);
      this._order$.next(this.sortOrder);
    }

    if (this.entries.length > 0) {
      this.calculateEntries();
    }

    this.draw();
  }

  /**
   * Calculate the new page and emit it
   */
  public draw(): this {
    this.log.trace('draw()');

    // Calculate page
    let start = (this.page - 1) * this.pageSize;
    let end = start + this.pageSize;

    const entries = this.filteredEntries.slice(start, end);
    const filtered = this.filteredEntries.length;
    const hidden = this.hiddenEntries.length;
    const total = this.entries.length;

    // Check if the current page contains values (after the user changes the filter it can happen
    // that the current page does not) and if not then go to the first page
    if (start > filtered) {
      this.goTo('first');

      start = 0;
      end = this.pageSize;
    }

    const toEmit = {
      allEntries: this.entries,
      entries,
      filtered,
      filteredEntries: this.filteredEntries,
      from: start + 1,
      hidden,
      hiddenEntries: this.hiddenEntries,
      page: this.page,
      // pages: Math.floor(filtered / this.pageSize),
      pages: this.pages,
      pageSize: this.pageSize,
      to: end > filtered ? filtered : end,
      total: total,
    };

    // Emit list of entries
    this._page$.next(toEmit);

    return this;
  }

  /**
   * Set the new filter
   * @param filter
   */
  public filter(filter?: string): this {
    this.filterValue = filter;
    this.updatedFilteredEntriesManually = false;

    this.calculateEntries();

    return this;
  }

  /**
   * Go to the defined page
   * @param pageDefinition
   */
  public goTo(pageDefinition: PageDefinition): this {
    let page: number;

    if (typeof pageDefinition === 'string') {
      page = this.resolve(pageDefinition);
    } else {
      page = pageDefinition;
    }

    this.page = page;

    return this;
  }

  public push(entry: T): this {
    if (this.updatedFilteredEntriesManually) {
      this.entries = this.entries.concat(entry);
      this.filteredEntries = this.filteredEntries.concat(entry);

      this.refreshEntries();
    } else {
      this.updateEntries([...this.entries, entry]);
    }

    this._lastInsertLocation =
      Math.floor(this.filteredEntries.findIndex((_) => _ === entry) / this.pageSize) + 1;

    return this;
  }

  public remove(entry: T): this {
    if (this.updatedFilteredEntriesManually) {
      this.entries = this.entries.filter((_) => _ !== entry);
      this.filteredEntries = this.filteredEntries.filter((_) => _ !== entry);

      this.refreshEntries();
    } else {
      this.updateEntries(withoutEntry(this.entries, entry));
    }

    this.checkPageAvailability();

    return this;
  }

  public sortBy(properties: string[] | string, reverse: boolean = false): this {
    if (Array.isArray(properties)) {
      this.sortOrder = properties.map((key) => {
        return {
          ignoreCase: true,
          key,
          order: reverse ? 'desc' : 'asc',
        };
      });
    } else {
      this.sortOrder = [
        {
          ignoreCase: true,
          key: properties,
          order: reverse ? 'desc' : 'asc',
        },
      ];
    }

    this._order$.next(this.sortOrder);

    this.calculateEntries();

    return this;
  }

  public updateEntries(entries: T[]): this {
    this.entries = entries;

    this.calculateEntries();

    return this;
  }

  public updateFilteredEntries(entries: T[]): this {
    this.filteredEntries = entries;
    this.hiddenEntries = this.entries.filter((entry) => entries.includes(entry) === false);
    this.updatedFilteredEntriesManually = true;

    if (this.sortOrder.length > 0) {
      this.filteredEntries.sort(sortedBy(...this.sortOrder));
    }

    this.refreshEntries();
    this.checkPageAvailability();

    return this;
  }

  /**
   * Set the page size, navigate to page 1 and emit the new page
   * @param pageSize
   */
  public updatePageSize(pageSize: number): this {
    this.pageSize = pageSize;

    return this;
  }

  /**
   * Calculate the list of entries using the current filter value and the sort properties.
   *
   * Since the user can update the filtered entries from the outside we cannot calculate them
   * automatically when the draw method is invoked. This is an issue but acceptable for now.
   */
  private calculateEntries() {
    const filter = this.filterValue;
    let entries = this.entries;

    if (this.entriesHaveMetaData(entries)) {
      entries = filterExclusive(entries, filter).values;
    } else if (this.entriesHaveKey(entries)) {
      entries = entries.filter((entry) => entry.key.toLowerCase().indexOf(filter) > -1);
    }

    if (this.sortOrder.length > 0) {
      entries = entries.slice().sort(sortBy(this.sortOrder));
    }

    this.filteredEntries = entries;
    this.hiddenEntries = this.entries.filter((entry) => entries.includes(entry) === false);
    this.pages = Math.ceil(entries.length / this.pageSize);
  }

  /**
   * Check if the page the user is currently on still exists. If not then go one page back.
   */
  private checkPageAvailability(): void {
    if (this.page > 1 && (this.page - 1) * this.pageSize >= this.filteredEntries.length) {
      this.goTo('last');
    }
  }

  private entriesHaveKey(entries: any[]): entries is (T & WithKey)[] {
    return entries[0]?.hasOwnProperty('key');
  }

  private entriesHaveMetaData(entries: any[]): entries is (T & WithMetadata)[] {
    return entries[0]?.hasOwnProperty('meta') && entries[0]?.meta instanceof Map;
  }

  private refreshEntries(): void {
    if (this.sortOrder.length > 0) {
      this.entries.sort(sortBy(this.sortOrder));
      this.filteredEntries.sort(sortBy(this.sortOrder));
    }

    this.hiddenEntries = this.entries.filter((entry) => this.filteredEntries.includes(entry) === false);
    this.pages = Math.ceil(this.filteredEntries.length / this.pageSize);
  }

  /**
   * Map a page definition to it's page
   * @param page
   */
  private resolve(page: string): number {
    const def = {
      first: 1,
      last: this.pages,
      next: this.page + 1,
      previous: this.page - 1,
    };

    return def[page] || 1;
  }
}
